/* eslint-disable no-console */
import {ReloadOutlined} from '@ant-design/icons';
import {Button, Divider, Flex} from 'antd';
import {isAxiosError} from 'axios';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import Loader from '../../components/Loader/Loader';
import PageContent from '../../components/PageContent/PageContent';
import MainTableTitle from '../../components/Table/Utils/MainTableTitle';
import {allRoutes} from '../../constants/Routes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import emissionCalculation from '../../services/modules/DASHBOARD/emissionCalculation';
import dataLineage, {
  dataLineageCsvExport
} from '../../services/modules/DataLineage/dataLineage';
import facilityDetailsAPI from '../../services/modules/OM/facilityDetails';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import showMessage from '../../utils/showMessage';
import {sortYearOptions} from '../orgManager/Utils/InfraStructureProfile/constant/sorYear';

import {buildGroupLabel, scopeToApiParam} from './utils/common';
import DataLineageFilter from './utils/DataLineageFilter';
import DataLineageTable from './utils/DataLineageTable';
import type {
  DataLineageColumnSearch,
  DataLineagePagination,
  DataLineageRow,
  DataLineageSort,
  DataLineageSortBy
} from './utils/dataLineageTypes';

const DEFAULT_PAGE_SIZE = 10;

const EMPTY_COLUMN_SEARCH: DataLineageColumnSearch = {};
const EMPTY_SORT: DataLineageSort = {};

const DataLineage: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const [isYearInitLoading, setIsYearInitLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [records, setRecords] = useState<DataLineageRow[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<string>('');
  const [scope, setScope] = useState<string>('');
  const [subFilters, setSubFilters] = useState<string>('');
  const [columnSearch, setColumnSearch] =
    useState<DataLineageColumnSearch>(EMPTY_COLUMN_SEARCH);
  const [sort, setSort] = useState<DataLineageSort>(EMPTY_SORT);
  const [pagination, setPagination] = useState<DataLineagePagination>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0
  });
  const [refreshing, setRefreshing] = useState<boolean>();
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  const resetQueryPage = useCallback(() => {
    setPagination((prev) => ({...prev, page: 1}));
  }, []);

  const resetAllQueryState = useCallback(() => {
    setSelectedFacility('');
    setScope('');
    setSubFilters('');
    setColumnSearch(EMPTY_COLUMN_SEARCH);
    setSort(EMPTY_SORT);
    setPagination((prev) => ({...prev, page: 1}));
  }, []);

  useEffect(() => {
    const {userDetails} = useUserStore.getState();
    facilityDetailsAPI(APITypeEnum.GET_ALL, true)
      .then((facilityDetails) => {
        const facilityYears = sortYearOptions(
          yearFilterArray(
            facilityDetails,
            !(
              hasUserRole(UserRole.Company_Admin) ||
              hasUserRole(UserRole.Company_user)
            )
              ? userDetails?.facility
              : undefined
          )
        );
        const firstYearMain = facilityYears?.[0]?.value;
        const params = new URLSearchParams(window.location.search);
        const searchYear = params.get('selectedYear') ?? '';
        const hasYear = facilityYears?.some(
          (i: {value: string}) => i.value === searchYear
        );
        const setYear = hasYear ? searchYear : firstYearMain ?? '';
        setSelectedYear(setYear);

        const isCompanyUser =
          hasUserRole(UserRole.Company_Admin) ||
          hasUserRole(UserRole.Company_user);

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('selectedYear', setYear);
          if (!isCompanyUser) {
            next.delete('facility');
          }

          return next;
        });
      })
      .catch((err) => {
        console.error('Error occurred:', err);
      })
      .finally(() => {
        setIsYearInitLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedYear) {
      return;
    }

    const abortController = new AbortController();
    setIsTableLoading(true);

    const query = {
      ...(selectedFacility ? {facilityId: selectedFacility} : {}),
      ...(scopeToApiParam(scope) ? {scope: scopeToApiParam(scope)} : {}),
      ...(subFilters ? {dataBy: subFilters} : {}),
      ...(columnSearch.group ? {group: columnSearch.group} : {}),
      ...(columnSearch.urlName ? {urlName: columnSearch.urlName} : {}),
      ...(sort.sortBy ? {sortBy: sort.sortBy} : {}),
      ...(sort.sortOrder ? {sortOrder: sort.sortOrder} : {}),
      page: pagination.page,
      pageSize: pagination.pageSize
    };

    dataLineage(selectedYear, query, {signal: abortController.signal})
      .then((res) => {
        const mappedRecords = res.records.map((item) => ({
          ...item,
          group: buildGroupLabel(item, {
            facilityId: selectedFacility,
            dataBy: subFilters
          })
        }));
        setRecords(mappedRecords);
        setPagination((prev) => ({
          ...prev,
          total: res.total,
          page: res.page,
          pageSize: res.pageSize
        }));
      })
      .catch((err) => {
        if (isAxiosError(err) && err.code === 'ERR_CANCELED') {
          return;
        }
        console.error('Error occurred:', err);
        setRecords([]);
        setPagination((prev) => ({...prev, total: 0}));
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsTableLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [
    selectedYear,
    refreshKey,
    selectedFacility,
    scope,
    subFilters,
    columnSearch,
    sort,
    pagination.page,
    pagination.pageSize
  ]);

  const handleYearChange = useCallback(
    (newYear: string) => {
      setSelectedYear(newYear);
      resetAllQueryState();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('selectedYear', newYear);

        return next;
      });
    },
    [resetAllQueryState, setSearchParams]
  );

  const handleFacilityChange = useCallback(
    (val: string) => {
      setSelectedFacility(val);
      resetQueryPage();
    },
    [resetQueryPage]
  );

  const handleScopeChange = useCallback(
    (val: string) => {
      setScope(val);
      setSubFilters('');
      resetQueryPage();
    },
    [resetQueryPage]
  );

  const handleSubFiltersChange = useCallback(
    (val: string) => {
      setSubFilters(val);
      resetQueryPage();
    },
    [resetQueryPage]
  );

  const handleColumnSearchChange = useCallback(
    (field: keyof DataLineageColumnSearch, value: string) => {
      setColumnSearch((prev) => ({
        ...prev,
        [field]: value || undefined
      }));
      resetQueryPage();
    },
    [resetQueryPage]
  );

  const handleSortChange = useCallback(
    (sortBy?: DataLineageSortBy, sortOrder?: 'asc' | 'desc') => {
      if (!sortBy || !sortOrder) {
        setSort(EMPTY_SORT);
      } else {
        setSort({sortBy, sortOrder});
      }
      resetQueryPage();
    },
    [resetQueryPage]
  );

  const handlePaginationChange = useCallback((page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      page: pageSize !== prev.pageSize ? 1 : page,
      pageSize
    }));
  }, []);

  const handleClearAll = useCallback(() => {
    resetAllQueryState();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('facility');

      return next;
    });
  }, [resetAllQueryState, setSearchParams]);

  const handleRefreshClick = useCallback(() => {
    setRefreshing(true);
    emissionCalculation(selectedYear)
      .then(() => {
        setRefreshKey((k) => k + 1);
      })
      .catch(() => {})
      .finally(() => {
        setRefreshing(false);
      });
  }, [selectedYear]);

  const handleDownloadCsv = useCallback(() => {
    if (!selectedYear || pagination.total === 0) {
      return;
    }

    const companyId = useCompanyStore.getState().companyId;
    const payload: {
      companyId: string;
      year: string;
      facilityId?: string;
    } = {
      companyId,
      year: selectedYear
    };

    if (selectedFacility) {
      payload.facilityId = selectedFacility;
    }

    setCsvDownloading(true);
    dataLineageCsvExport(payload)
      .then((response) => {
        if (
          response.data.status === 'success' &&
          response.data.data?.url
        ) {
          window.open(response.data.data.url, '_blank');

          return;
        }

        showMessage({
          status: 'error',
          message: response.data.message
        });
      })
      .catch((err) => {
        if (isAxiosError(err) && err.response?.data?.message) {
          showMessage({
            status: 'error',
            message: err.response.data.message
          });

          return;
        }

        showMessage();
      })
      .finally(() => {
        setCsvDownloading(false);
      });
  }, [selectedYear, selectedFacility, pagination.total]);

  const hasActiveQuery =
    Boolean(selectedFacility) ||
    Boolean(scope) ||
    Boolean(subFilters) ||
    Boolean(columnSearch.group) ||
    Boolean(columnSearch.urlName) ||
    Boolean(sort.sortBy);

  if (isYearInitLoading && !selectedYear) {
    return <Loader isHome={true} />;
  }

  return (
    <PageContent>
      <Flex justify="space-between" align="center" style={{width: '100%'}}>
        <MainTableTitle title="Data Lineage" showDivider={false} />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            onClick={() => {
              navigate(`${allRoutes.OM.infraProfile}`);
            }}
            type="primary"
          >
            Upload activity data
          </Button>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            title="Reflecting data from Carbon Accounting"
            loading={refreshing}
            onClick={handleRefreshClick}
          ></Button>
        </Flex>
      </Flex>
      <Divider />
      <DataLineageFilter
        selectedYear={selectedYear}
        setSelectedYear={handleYearChange}
        setSelectedFacility={handleFacilityChange}
        scope={scope}
        setScope={handleScopeChange}
        setSubFilters={handleSubFiltersChange}
        shouldDisable={refreshing}
        onDownloadCsv={handleDownloadCsv}
        csvDownloading={csvDownloading}
        hasLineageData={pagination.total > 0}
        isLineageLoading={isTableLoading}
        onClearAll={handleClearAll}
        hasActiveQuery={hasActiveQuery}
      />

      <DataLineageTable
        records={records}
        loading={isTableLoading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total
        }}
        sort={sort}
        columnSearch={columnSearch}
        onPaginationChange={handlePaginationChange}
        onSortChange={handleSortChange}
        onColumnSearchChange={handleColumnSearchChange}
      />
    </PageContent>
  );
};

export default DataLineage;
